import type {
  LoyaltyPassCategoryRef,
  LoyaltyPassProgramConfig,
  LoyaltyPassRule,
  LoyaltyPassRuleReward,
  LoyaltyPassRuleType,
} from '../../types/loyaltyPass';
import {
  LOYALTY_PASS_RULE_MAX_VISITS,
  LOYALTY_PASS_RULE_MIN_CATEGORIES,
  LOYALTY_PASS_RULE_MIN_VISITS,
} from '../../types/loyaltyPass';

export type LoyaltyPassRuleValidationError =
  | 'loyaltyPass.rules.errors.NAME_REQUIRED'
  | 'loyaltyPass.rules.errors.CATEGORIES_REQUIRED'
  | 'loyaltyPass.rules.errors.VISITS_OUT_OF_RANGE'
  | 'loyaltyPass.rules.errors.MIN_DISTINCT_INVALID'
  | 'loyaltyPass.rules.errors.REWARD_LABEL_REQUIRED'
  | 'loyaltyPass.rules.errors.REWARD_VALUE_REQUIRED'
  | 'loyaltyPass.rules.errors.REWARD_VALUE_INVALID'
  | 'loyaltyPass.rules.errors.TYPE_INVALID';

function categoryKey(ref: LoyaltyPassCategoryRef): string {
  return `${ref.kind}:${ref.categoryId}`;
}

export function clampLoyaltyPassRuleVisits(n: number): number {
  if (!Number.isFinite(n)) return LOYALTY_PASS_RULE_MIN_VISITS;
  return Math.min(
    LOYALTY_PASS_RULE_MAX_VISITS,
    Math.max(LOYALTY_PASS_RULE_MIN_VISITS, Math.floor(n))
  );
}

export function normalizeLoyaltyPassCategoryRef(
  raw: Partial<LoyaltyPassCategoryRef> | null | undefined
): LoyaltyPassCategoryRef | null {
  if (!raw) return null;
  const kind = raw.kind === 'product' || raw.kind === 'service' ? raw.kind : null;
  const categoryId =
    typeof raw.categoryId === 'string' ? raw.categoryId.trim() : '';
  if (!kind || !categoryId) return null;
  return { kind, categoryId };
}

export function normalizeLoyaltyPassRuleReward(
  raw: Partial<LoyaltyPassRuleReward> | null | undefined,
  ruleType?: LoyaltyPassRuleType
): LoyaltyPassRuleReward {
  const type = raw?.type === 'fixed' ? 'fixed' : 'percent';
  const label = typeof raw?.label === 'string' ? raw.label.trim() : '';
  const value =
    typeof raw?.value === 'number' && Number.isFinite(raw.value) ? raw.value : 0;
  const defaultBase =
    ruleType === 'visit_count' || ruleType === 'category_count'
      ? 'whole_ticket'
      : 'eligible_lines';
  const discountBase =
    raw?.discountBase === 'whole_ticket' || raw?.discountBase === 'eligible_lines'
      ? raw.discountBase
      : defaultBase;
  return { type, label, value, discountBase };
}

function resolveRuleType(
  raw: string | undefined
): Exclude<LoyaltyPassRuleType, 'category_count'> {
  if (raw === 'ticket_multi_category') return 'ticket_multi_category';
  // category_count legacy → visit_count
  return 'visit_count';
}

export function normalizeLoyaltyPassRule(
  input: Partial<LoyaltyPassRule> | null | undefined,
  index = 0
): LoyaltyPassRule {
  const type = resolveRuleType(input?.type);
  const categoriesRaw = Array.isArray(input?.categories) ? input!.categories : [];
  const seen = new Set<string>();
  let categories: LoyaltyPassCategoryRef[] = [];
  if (type === 'ticket_multi_category') {
    for (const c of categoriesRaw) {
      const ref = normalizeLoyaltyPassCategoryRef(c);
      if (!ref) continue;
      const key = categoryKey(ref);
      if (seen.has(key)) continue;
      seen.add(key);
      categories.push(ref);
    }
  }

  const id =
    typeof input?.id === 'string' && input.id.trim().length > 0
      ? input.id.trim()
      : `rule_${index + 1}`;

  const visitsRequired = clampLoyaltyPassRuleVisits(
    typeof input?.visitsRequired === 'number' ? input.visitsRequired : 1
  );

  return {
    id,
    enabled: input?.enabled !== false,
    type,
    priority:
      typeof input?.priority === 'number' && Number.isFinite(input.priority)
        ? Math.floor(input.priority)
        : index,
    categories,
    visitsRequired,
    reward: normalizeLoyaltyPassRuleReward(input?.reward, type),
    name: typeof input?.name === 'string' ? input.name.trim() : '',
  };
}

export function normalizeLoyaltyPassRules(
  raw: unknown
): LoyaltyPassRule[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((r, i) =>
    normalizeLoyaltyPassRule(
      r && typeof r === 'object' ? (r as Partial<LoyaltyPassRule>) : {},
      i
    )
  );
}

export function validateLoyaltyPassRule(
  rule: LoyaltyPassRule
): LoyaltyPassRuleValidationError | null {
  if (!rule.name.trim()) return 'loyaltyPass.rules.errors.NAME_REQUIRED';
  if (
    !Number.isFinite(rule.visitsRequired) ||
    rule.visitsRequired < LOYALTY_PASS_RULE_MIN_VISITS ||
    rule.visitsRequired > LOYALTY_PASS_RULE_MAX_VISITS
  ) {
    return 'loyaltyPass.rules.errors.VISITS_OUT_OF_RANGE';
  }
  if (rule.type === 'ticket_multi_category') {
    if (rule.categories.length < LOYALTY_PASS_RULE_MIN_CATEGORIES) {
      return 'loyaltyPass.rules.errors.CATEGORIES_REQUIRED';
    }
  } else if (rule.type !== 'visit_count' && rule.type !== 'category_count') {
    return 'loyaltyPass.rules.errors.TYPE_INVALID';
  }
  if (!rule.reward.label.trim()) {
    return 'loyaltyPass.rules.errors.REWARD_LABEL_REQUIRED';
  }
  const v = rule.reward.value;
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    return 'loyaltyPass.rules.errors.REWARD_VALUE_REQUIRED';
  }
  if (rule.reward.type === 'percent' && (v < 1 || v > 100)) {
    return 'loyaltyPass.rules.errors.REWARD_VALUE_INVALID';
  }
  if (rule.reward.type === 'fixed' && v < 1) {
    return 'loyaltyPass.rules.errors.REWARD_VALUE_INVALID';
  }
  return null;
}

export function validateLoyaltyPassRules(
  rules: LoyaltyPassRule[]
): LoyaltyPassRuleValidationError | null {
  for (const rule of rules) {
    if (!rule.enabled) continue;
    const err = validateLoyaltyPassRule(rule);
    if (err) return err;
  }
  return null;
}

/** True si le programme utilise le moteur multi-règles. */
export function isLoyaltyPassMultiRulesMode(
  program: LoyaltyPassProgramConfig | null | undefined
): boolean {
  const rules = normalizeLoyaltyPassRules(program?.rules);
  return rules.some((r) => r.enabled);
}

/**
 * Chevauchement de cibles catégories.
 * `visit_count` (cats []) n’utilise pas cette helper pour vs multi
 * (D-MR5.1 : tout visit_count est suppressé si un multi award).
 */
export function categoryRefsOverlap(
  a: LoyaltyPassCategoryRef[],
  b: LoyaltyPassCategoryRef[]
): boolean {
  if (a.length === 0 || b.length === 0) return false;
  const setB = new Set(b.map(categoryKey));
  return a.some((ref) => setB.has(categoryKey(ref)));
}

/**
 * Warning UI : ≥2 multi-cat actives dont les cibles s’intersectent.
 */
export function hasOverlappingCategoryCountRules(
  rules: LoyaltyPassRule[]
): boolean {
  const multi = rules.filter(
    (r) =>
      r.enabled &&
      r.type === 'ticket_multi_category' &&
      r.categories.length > 0
  );
  for (let i = 0; i < multi.length; i += 1) {
    const left = multi[i];
    if (!left) continue;
    for (let j = i + 1; j < multi.length; j += 1) {
      const right = multi[j];
      if (!right) continue;
      if (categoryRefsOverlap(left.categories, right.categories)) {
        return true;
      }
    }
  }
  return false;
}

export { categoryKey as loyaltyPassCategoryKey };
